import React, {ChangeEvent, forwardRef, ReactElement, useState} from 'react';
import {DefaultTheme} from 'styled-components';
import {
  ErrorText,
  IconWrapper,
  InputBlockWrapper,
  InputWrapper,
  Label,
} from './Input.styles';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';

export interface InputProps {
  id: string;
  name?: string;
  title?: string;
  min?: number;
  defaultValue?: string | number;
  max?: number;
  size?: number;
  type?: string;
  step?: string;
  placeholder?: string;
  value?: string | number;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: any) => void;
  testID?: string;
  label?: string;
  className?: string;
  style?: Record<string, any>;
  labelBGColor?: string;
  borderColor?: string;
  labelColor?: string;
  textColor?: string;
  error?: string | (() => string);
  hint?: string;
  maxLength?: number;
  withoutPadding?: boolean;
  placeholderColor?: string;
  placeholderFontSize?: number;
  iconSrc?: string;
  iconElement?: ReactElement;
  onClickIcon?: () => void;
  readonly?: boolean;
  disabled?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  rounded?: boolean;
  turnAutocomplete?: string;
  withoutErrorText?: boolean;
  width?: string;
}

export interface StyledInputProps {
  labelBGColor?: string;
  labelColor?: string;
  borderColor?: string;
  textColor?: string;
  focus?: boolean;
  theme?: DefaultTheme;
  error?: string | undefined;
  withoutPadding?: boolean;
  placeholderColor?: string;
  placeholderFontSize?: number;
  rounded?: boolean;
  inputHaveValue?: boolean;
  placeholderLength?: number;
  disabled?: boolean;
  width?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
    type = 'string',
    step,
    value,
    defaultValue,
    onChange,
    onFocus = () => false,
    onBlur,
    min,
    max,
    placeholder,
    id,
    name,
    testID,
    label,
    style,
    labelBGColor,
    borderColor,
    labelColor,
    textColor,
    error,
    hint,
    maxLength = 524288,
    withoutPadding = false,
    className,
    placeholderColor,
    placeholderFontSize,
    iconSrc,
    iconElement,
    onClickIcon,
    readonly = false,
    disabled = false,
    onKeyDown,
    rounded,
    turnAutocomplete = 'on',
    withoutErrorText = false,
    width,
  }) => {
    const [isFocus, setIsFocus] = useState(false);

    return (
      <InputBlockWrapper width={width}>
        <InputWrapper
          onFocus={e => {
            onFocus(e);
            setIsFocus(!isFocus);
          }}
          focus={isFocus}
          error={typeof error === 'function' ? error() : error}
          style={style}
          borderColor={borderColor!}
          labelColor={labelColor!}
          textColor={textColor!}
          className={className}
          withoutPadding={withoutPadding}
          placeholderColor={placeholderColor!}
          placeholderFontSize={placeholderFontSize!}
          inputHaveValue={String(value).length !== 0}
          placeholderLength={placeholder?.length}
          rounded={rounded}
          title={label}
          disabled={disabled}>
          <Label
            htmlFor={id}
            labelBGColor={labelBGColor!}
            labelColor={labelColor!}
            error={typeof error === 'function' ? error() : error}
            inputHaveValue={value === 0 || !!value}
            focus={isFocus}
            disabled={disabled}>
            {label}
          </Label>
          <input
            id={id}
            name={name}
            type={type}
            step={step}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            data-testid={testID}
            min={min}
            max={type === 'date' ? '9999-12-31' : max}
            maxLength={maxLength}
            placeholder={placeholder}
            onBlur={e => {
              setIsFocus(false);
              if (onBlur) {
                onBlur(e);
              }
            }}
            readOnly={readonly}
            disabled={disabled}
            onKeyDown={onKeyDown}
            autoComplete={turnAutocomplete}
          />
          {!withoutErrorText && error && (
            <ErrorText>{typeof error === 'function' ? error() : error}</ErrorText>
          )}
          {!error && iconSrc && (
            <IconWrapper onClick={onClickIcon}>
              <img src={iconSrc} width={24} height={24} alt="inputIcon" />
            </IconWrapper>
          )}
          {iconElement && <IconWrapper onClick={onClickIcon}>{iconElement}</IconWrapper>}
        </InputWrapper>
        {!!hint && (
          <Text text={hint} type="tinyR" color={theme.colors.basic03} align="right" />
        )}
      </InputBlockWrapper>
    );
  },
);

Input.displayName = 'Input';

export default Input;
